import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { FulfillmentStopReason } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';

import { FieldLabelComponent } from '../components/common/field-label.component';
import { STOP_REASON_ICON, STOP_REASON_LABEL_KEY } from '../components/common/guarantee-vocabulary';
import { ToggleDirective } from '../components/common/toggle.directive';
import { DIALOG_CLASSES } from '../components/ui/ui.classes';

/** Every ground a booking can end on, taken from the enum so the list cannot drift from it. */
export const STOP_REASONS = Object.values(FulfillmentStopReason);

export interface FulfillmentOutcomeData {
  titleKey: string;
  /** What the outcome is being recorded against, named so the question is about this record. */
  subject: string;
  /**
   * Whether "it happened" is one of the answers. Off where only the ending is on offer — cancelling
   * a whole course says nothing about care that was delivered.
   */
  offersFulfilled: boolean;
  /** How many lines the answer will be written to, stated only when it is more than one. */
  count?: number;
}

/**
 * Which way it went, and — where it ended — the ground it ended on. The ground is owed by exactly
 * one of the two answers, so it is optional here and always set beside `fulfilled: false`. A
 * discriminated union would say that in the type, but this project compiles without
 * `strictNullChecks` and would not narrow it, leaving every caller asserting instead of reading.
 */
export interface FulfillmentOutcomeResult {
  fulfilled: boolean;
  stopReason?: FulfillmentStopReason;
}

/**
 * Asked when a booking is settled: the care happened, or it did not and here is why.
 *
 * One dialog for both answers because it is one question. Splitting it would put "mark delivered"
 * and "stop" on the menu as two rows that are only ever asked together, and a manager would have to
 * decide which to open before deciding what happened.
 */
export function openFulfillmentOutcome(dialog: MatDialog, data: FulfillmentOutcomeData): Observable<FulfillmentOutcomeResult | undefined> {
  return dialog
    .open<FulfillmentOutcomeComponent, FulfillmentOutcomeData, FulfillmentOutcomeResult>(FulfillmentOutcomeComponent, {
      width: '90vw',
      maxWidth: '440px',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    })
    .afterClosed()
    .pipe(take(1));
}

@Component({
  selector: 'guarantees-fulfillment-outcome',
  standalone: true,
  imports: [MatIcon, TranslatePipe, FieldLabelComponent, ToggleDirective],
  templateUrl: './fulfillment-outcome.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FulfillmentOutcomeComponent {
  /** Heading, scrolling body and pinned acts — the one dialog shape this module uses. */
  protected readonly layout = DIALOG_CLASSES;

  private readonly dialogRef = inject(MatDialogRef<FulfillmentOutcomeComponent, FulfillmentOutcomeResult>);

  protected readonly data = inject<FulfillmentOutcomeData>(MAT_DIALOG_DATA);

  protected readonly stopReasons = STOP_REASONS;
  protected readonly stopReasonLabelKey = STOP_REASON_LABEL_KEY;
  protected readonly stopReasonIcon = STOP_REASON_ICON;

  /**
   * Unanswered until the manager says so: which way it went is the whole question, and defaulting it
   * would let the dialog be confirmed without one having been asked.
   *
   * Pre-answered where there is nothing to choose — an ending is the only outcome on offer.
   */
  protected readonly fulfilled = signal<boolean | undefined>(this.data.offersFulfilled ? undefined : false);

  protected readonly stopReason = signal<FulfillmentStopReason>(FulfillmentStopReason.Cancelled);

  /** The ground belongs to the answer that has one, so it is asked only once that answer is given. */
  protected readonly collectsStopReason = computed(() => this.fulfilled() === false);

  protected readonly canConfirm = computed(() => this.fulfilled() !== undefined);

  /**
   * What the committing button is about to do, so it names the answer rather than the question.
   * While unanswered it stays neutral: a button promising one of the two outcomes before either has
   * been chosen reads as the answer already having been given.
   */
  protected readonly confirmClass = computed(() => {
    if (!this.canConfirm()) {
      return '';
    }

    return this.fulfilled() ? 'bg-success-600 hover:bg-success-700' : 'bg-error-600 hover:bg-error-700';
  });
  protected readonly confirmIcon = computed(() => (this.canConfirm() ? (this.fulfilled() ? 'task_alt' : 'stop_circle') : 'help'));
  protected readonly confirmKey = computed(() => {
    if (!this.canConfirm()) {
      return 'guaranteeV2.message.outcomeRequired';
    }

    return this.fulfilled() ? 'guaranteeV2.action.markFulfilled' : 'guaranteeV2.action.stopFulfillment';
  });

  protected setFulfilled(value: boolean): void {
    this.fulfilled.set(value);
  }

  protected confirm(): void {
    if (!this.canConfirm()) {
      return;
    }

    this.dialogRef.close({ fulfilled: !!this.fulfilled(), stopReason: this.collectsStopReason() ? this.stopReason() : undefined });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
